import { useCallback, useEffect, useState } from 'react';
import type { PublicShopifyQuery } from '@discovr/features';
import { SEARCH_DEBOUNCE_MS } from './hooks/useRosterSearch';
import { DEFAULT_PAGE_SIZE } from './useCampaignPageSizeParam';

const INITIAL_QUERY: PublicShopifyQuery = {
  page: 1,
  limit: DEFAULT_PAGE_SIZE,
  live: 'all',
  search: '',
};

export type PublicShopifyQueryPatch = Partial<Omit<PublicShopifyQuery, 'search'>>;

export interface PublicShopifyQueryState {
  /** What the share view is fetched with. `search` is the term actually applied. */
  query: PublicShopifyQuery;
  /** What the search box shows: follows every keystroke. */
  searchInput: string;
  setSearchInput: (value: string) => void;
  setQuery: (patch: PublicShopifyQueryPatch) => void;
}

/** Page, size, live filter and search of the public Shopify coupons list.
 *  The share link is not a router location, so this lives in component state.
 *  A new search term is applied after the typing pause together with a return
 *  to page 1, as one change, so it costs one request; clearing applies at once. */
export function usePublicShopifyQuery(): PublicShopifyQueryState {
  const [query, setFull] = useState<PublicShopifyQuery>(INITIAL_QUERY);
  const [searchInput, setInput] = useState('');
  const trimmed = searchInput.trim();

  const setQuery = useCallback(
    (patch: PublicShopifyQueryPatch) => setFull((prev) => ({ ...prev, ...patch })),
    [],
  );
  const applySearch = useCallback(
    (search: string) =>
      setFull((prev) => (prev.search === search ? prev : { ...prev, search, page: 1 })),
    [],
  );
  const setSearchInput = useCallback(
    (value: string) => {
      setInput(value);
      if (value.trim() === '') applySearch('');
    },
    [applySearch],
  );

  useEffect(() => {
    if (trimmed === '') return;
    const id = setTimeout(() => applySearch(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [trimmed, applySearch]);

  return { query, searchInput, setSearchInput, setQuery };
}
